// src/pages/Reports/index.tsx
import { useState, useEffect } from "react";
import { Tabs, Tab, styled, CircularProgress } from "@mui/material";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import { useNavigate } from "react-router-dom";
import actionsHandler from "@/store/actions/actionHandler";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import ReportsTable from "./components/ReportsTable";
import PlusIcon from "@styles/all-images/icons/plus.svg";
import { UserTierType } from "@/constants/enums/user-tier-type.enum";
import { usePermissionState } from "@/providers/PermissionProvider";
import { ReportsPermissions } from "@/constants/permission-modules/base-permissions";

// Styled components for tabs
const StyledTabs = styled(Tabs)({
  minHeight: "unset",
  "& .MuiTabs-indicator": {
    display: "none",
  },
});

const StyledTab = styled(Tab)({
  minHeight: "unset",
  padding: "8px 16px",
  textTransform: "none",
  fontSize: "14px",
  fontWeight: 600,
  minWidth: "unset",
});

const tabs = [
  { label: "Daily", type: "daily", value: "1" },
  { label: "Weekly", type: "weekly", value: "2" },
  { label: "Monthly", type: "monthly", value: "3" },
];

interface FetchParams {
  type: string;
  limit?: number;
  page?: number;
  searchTerm?: string;
  sortColumn?: string;
  sortDirection?: string;
  companyId?: number | null;
}

const Reports = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch<AppThunkDispatch>();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [userType, setUserType] = useState<UserTierType>(UserTierType.Tier3);
  const { isPermitted } = usePermissionState();

  useEffect(() => {
    let isMounted = true;

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const currentUserType = (user.user_type?.id ||
      UserTierType.Tier1) as UserTierType;
    setUserType(currentUserType);

    const fetchInitialData = async () => {
      if (!isMounted) return;
      setLoading(true);

      try {
        await fetchReportsData({
          type: tabs[activeTab].type,
          page: 1,
          limit: 10,
        });
      } catch (error) {
        console.error("Error fetching initial reports:", error);
      } finally {
        if (isMounted) {
          setLoading(false);
          setIsInitialLoad(false);
        }
      }
    };

    // Only fetch on initial load or tab change (not both)
    if (isInitialLoad || !isInitialLoad) {
      fetchInitialData();
    }

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [activeTab]); // Only depend on activeTab changes

  const fetchReportsData = async (params: FetchParams) => {
    try {
      const { authenticatedCall } = actionsHandler("reports");
      const queryParams = new URLSearchParams({
        report_type: tabs[activeTab].value,
        ...(params.companyId && { companies: params.companyId.toString() }),
        limit: (params.limit || 10).toString(),
        page: (params.page || 1).toString(),
        ...(params.searchTerm && { search: params.searchTerm }),
        ...(params.sortColumn && {
          ordering:
            (params.sortDirection === "desc" ? "-" : "") + params.sortColumn,
        }),
      }).toString();
      const response = await dispatch(
        authenticatedCall({
          method: "get",
          customQuery: queryParams,
        }) as any,
      );
      return response;
    } catch (error) {
      console.error("Error fetching reports:", error);
      return {};
    }
  };

  return (
    <div className="tw-relative">
      {loading ? (
        <div className="tw-flex tw-justify-center tw-items-center tw-h-screen">
          <CircularProgress />
        </div>
      ) : (
        <>
          <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
            <div className="tw-px-6 tw-pt-4 tw-bg-white tw-border-b tw-border-[#E9EAEB]">
              <div className="tw-flex tw-justify-between tw-items-center">
                <div className="tw-flex tw-items-center">
                  <img
                    src={BackIcon}
                    alt="Back Icon"
                    onClick={() => navigate("/menu")}
                    className="tw-cursor-pointer"
                  />
                  <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold tw-text-[#232323]">
                    Stakeholder Reports
                  </h1>
                </div>
                {userType === UserTierType.Tier3 &&
                  isPermitted(ReportsPermissions.CREATE_REPORTS) && (
                    <button
                      className="primary-custom-button tw-flex tw-items-center tw-text-white tw-px-4 tw-py-2 tw-rounded"
                      onClick={() => navigate("/reports/add")}
                    >
                      <img
                        src={PlusIcon}
                        alt="Add Icon"
                        className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                      />
                      Add Report
                    </button>
                  )}
              </div>

              <div className="tw-w-fit tw-mt-2 tw-border tw-border-[#E9EAEB]">
                <StyledTabs
                  value={activeTab}
                  onChange={(e, newValue) => {
                    setActiveTab(newValue);
                  }}
                >
                  {tabs.map((tab, index) => (
                    <StyledTab
                      key={index}
                      label={tab.label}
                      className={`tw-text-sm tw-font-semibold ${
                        activeTab === index ? "active-tab" : "tw-text-[#232323]"
                      } tab-border`}
                    />
                  ))}
                </StyledTabs>
              </div>
            </div>
          </div>
          <div className="tw-mt-[177px]">
            <ReportsTable
              userType={userType}
              type={tabs[activeTab].type}
              fetchData={fetchReportsData}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default Reports;
